/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
import { generateFileName } from 'utils/common';
import { get } from 'lodash';
import AbstractKafkaCodeSnippet from './AbstractKafkaCodeSnippet';
import { SASL_MECHANISMS, SECURITY_PROTOCOLS, SASL_JAAS_MODULES } from '../constants';

export default class JavaKafkaCodeSnippet extends AbstractKafkaCodeSnippet {
  getByteDeserializer() {
    return {
      deserializerClass: 'org.apache.kafka.common.serialization.ByteArrayDeserializer',
      deserializerType: 'byte[]'
    };
  }

  getDeserializerMappings(type = 'string', format = 'default') {
    let deserializerClass;
    let deserializerType;
    type = type.toLowerCase();
    switch (type) {
      case 'byte':
        ({ deserializerClass, deserializerType } = this.getByteDeserializer());
        break;
      case 'integer':
        switch (format) {
          case 'int64':
            deserializerClass = 'org.apache.kafka.common.serialization.LongDeserializer';
            deserializerType = 'Long';
            break;
          case 'int32':
          default:
            deserializerClass = 'org.apache.kafka.common.serialization.IntegerDeserializer';
            deserializerType = 'Integer';
            break;
        }
        break;
      case 'number':
        switch (format) {
          case 'double':
            deserializerClass = 'org.apache.kafka.common.serialization.DoubleDeserializer';
            deserializerType = 'Double';
            break;
          case 'float':
          default:
            deserializerClass = 'org.apache.kafka.common.serialization.FloatDeserializer';
            deserializerType = 'Float';
        }
        break;
      case 'object':
        deserializerClass = this.translate('custom_deserializer_class');
        deserializerType = 'Object';
        break;
      // eslint-disable-next-line default-case-last
      default:
      case 'string':
        deserializerClass = 'org.apache.kafka.common.serialization.StringDeserializer';
        deserializerType = 'String';
        break;
    }

    return { deserializerClass, deserializerType };
  }

  getKeyDeserializer() {
    const kafkaKey = {};
    const message = this.apiHandler.getMessage(this.channelName, 'subscribe');
    const messageBindings = this.apiHandler.getMessageBindings(message);
    let type;
    const key = get(messageBindings, 'kafka.key') ?? get(messageBindings, '[0]?.value().key');

    if (key) {
      type = typeof key === 'string' ? key : key.type;
    } else {
      type = 'string';
    }

    const { deserializerClass, deserializerType } = this.getDeserializerMappings(type);
    kafkaKey.deserializerClass = deserializerClass;
    kafkaKey.deserializerType = deserializerType;
    if (deserializerType === 'Object') {
      kafkaKey.kafkaKeyHelp = this.translate('kafka_key_help');
    }
    return kafkaKey;
  }

  getSchemaInformation() {
    const schemaSnippetOptions = {
      isAvro: false,
      isPrimitive: true,
      doSomething: this.translate('do_something_default'),
      fileName: null
    };
    const { payload: schemaPayload, format: schemaFormat, type: schemaType } = this.apiHandler.getFirstSchema(this.channelName, this.operationId);
    let type;
    let format;

    if (schemaFormat && schemaPayload) {
      schemaSnippetOptions.isPrimitive = this.apiHandler.isPrimitiveSchema(schemaPayload);

      if (schemaSnippetOptions.isPrimitive) {
        ({ type, format } = this.apiHandler.getPrimitiveSchemaInfo(schemaPayload));
      } else if (this.apiHandler.isAvroSchema(schemaType)) {
        type = this.DEFAULT_AVRO_DESERIALIZER_TYPE;
        schemaSnippetOptions.doSomething = this.translate('do_something_avro');
      } else if (schemaPayload && schemaPayload.type === 'object') {
        type = this.DEFAULT_ASYNC_API_DESERIALIZER_TYPE;
        schemaSnippetOptions.doSomething = this.translate('do_something_asyncapi');
      }

      if (this.apiHandler.isAvroSchema(schemaType)) {
        schemaSnippetOptions.fileName = generateFileName(this.channelName, 'avsc');
        schemaSnippetOptions.isAvro = true;
      }
    } else {
      type = 'string';
    }
    const { deserializerClass, deserializerType } = this.getDeserializerMappings(type, format);
    schemaSnippetOptions.deserializerClass = deserializerClass;
    schemaSnippetOptions.deserializerType = deserializerType;

    return schemaSnippetOptions;
  }

  getBootstrapServers() {
    return this.apiHandler
      .getEndpoints()
      .map(({ url }) => url)
      .join(',');
  }

  sasl_ssl() {
    return {
      SASL: true,
      SSL: true,
      saslMechanism: SASL_MECHANISMS.PLAIN,
      saslJaasModule: SASL_JAAS_MODULES.PLAIN,
      securityProtocol: SECURITY_PROTOCOLS.SASL_SSL
    };
  }

  sasl_plain() {
    return {
      SASL: true,
      saslMechanism: SASL_MECHANISMS.PLAIN,
      saslJaasModule: SASL_JAAS_MODULES.PLAIN,
      securityProtocol: SECURITY_PROTOCOLS.SASL_PLAINTEXT
    };
  }

  sasl_sha_256() {
    return {
      SASL: true,
      saslMechanism: SASL_MECHANISMS.SCRAM256,
      saslJaasModule: SASL_JAAS_MODULES.SCRAM,
      securityProtocol: SECURITY_PROTOCOLS.SASL_PLAINTEXT
    };
  }

  sasl_sha_512() {
    return {
      SASL: true,
      saslMechanism: SASL_MECHANISMS.SCRAM512,
      saslJaasModule: SASL_JAAS_MODULES.SCRAM,
      securityProtocol: SECURITY_PROTOCOLS.SASL_PLAINTEXT
    };
  }

  sasl_ssl_sha_256() {
    return {
      SASL: true,
      SSL: true,
      saslMechanism: SASL_MECHANISMS.SCRAM256,
      saslJaasModule: SASL_JAAS_MODULES.SCRAM,
      securityProtocol: SECURITY_PROTOCOLS.SASL_SSL
    };
  }

  sasl_ssl_sha_512() {
    return {
      SASL: true,
      SSL: true,
      saslMechanism: SASL_MECHANISMS.SCRAM512,
      saslJaasModule: SASL_JAAS_MODULES.SCRAM,
      securityProtocol: SECURITY_PROTOCOLS.SASL_SSL
    };
  }

  ssl() {
    return {
      SSL: true,
      securityProtocol: SECURITY_PROTOCOLS.SSL
    };
  }

  ssl_mutualtls() {
    return {
      MUTUAL_TLS: true,
      SSL: true,
      securityProtocol: SECURITY_PROTOCOLS.SSL
    };
  }

  plaintext() {
    return {
      securityProtocol: SECURITY_PROTOCOLS.PLAINTEXT
    };
  }
}
